import { createSelector } from "reselect";
import { IApplication } from "shared/model/IApplication";
import {
  ICoworkBranch,
  ICoworkSpace,
  UserRoles
} from "shared/model/ICoworkSpace";

import { objectSubset } from "../../model/SharedTypes";
import AuthSelectors from "../auth/AuthSelectors";
import { IState } from "./..";
import { IdToObjectMap } from "./../../model/SharedTypes";
import { IApplicationsState } from "./applications";

export default class ApplicationsSelectors {
  public static currentCowork = (state: IApplicationsState) =>
    state.currentCoworkSpace;
  public static currentCoworkName = (state: IApplicationsState) =>
    state.currentCoworkSpace ? state.currentCoworkSpace.name : undefined;
  public static currentCoworkDisplayName = (state: IApplicationsState) =>
    state.currentCoworkSpace ? state.currentCoworkSpace.displayName : undefined;

  public static isFetchingApplications = (state: IApplicationsState) =>
    state.applicationsForCurrentCowork.isFetching;
  public static didFailFetchingApplications = (state: IApplicationsState) =>
    Boolean(state.applicationsForCurrentCowork.error);

  // tslint:disable-next-line:member-ordering
  public static applicationsForCurrentUser = createSelector(
    (state: IApplicationsState) => state.applications,
    (state: IApplicationsState) => state.applicationsForCurrentCowork.ids,
    (
      applications: IdToObjectMap<IApplication>,
      applicationIds: string[]
    ): IApplication[] => {
      return objectSubset<IApplication>(applications, applicationIds);
    }
  );

  // tslint:disable-next-line:member-ordering
  public static applicationsCountForCurrentCowork = createSelector(
    ApplicationsSelectors.applicationsForCurrentUser,
    (applications: IApplication[]): number => {
      return applications ? applications.length : 0;
    }
  );

  public static applicationCreationInProgress = (state: IApplicationsState) =>
    state.creation && state.creation.inProgress;

  public static applicationCreationError = (state: IApplicationsState) =>
    state.creation && Boolean(state.creation.error);

  // tslint:disable-next-line:member-ordering
  public static createdApplication = createSelector(
    (state: IApplicationsState) => state.applications,
    (state: IApplicationsState) => state.lastCreatedApplicationId,
    (
      applications: IdToObjectMap<IApplication>,
      applicationId?: string
    ): IApplication | undefined => {
      if (applicationId) {
        return objectSubset<IApplication>(applications, [applicationId])[0];
      } else {
        return undefined;
      }
    }
  );

  public static currentCoworkBranch = (state: IApplicationsState) =>
    state.currentCoworkBranch;

  public static branchForCurrentUserInCowork = (
    userEmail?: string,
    cowork?: ICoworkSpace
  ) => {
    if (!userEmail || !cowork) {
      return null;
    }

    const { roles } = cowork;
    if (roles) {
      const currentUserRole = roles[userEmail];
      if (currentUserRole === UserRoles.Admin) {
        return null;
      } else if (
        currentUserRole instanceof Array &&
        currentUserRole.length > 0
      ) {
        // TODO: we select just the first branch for now.
        return currentUserRole[0];
      } else {
        throw new Error(`Bad roles object ${roles}`);
      }
    } else {
      return null;
    }
  };

  public static canCurrentUserSelectFromMultipleBranches = (
    state: IState
  ): boolean => {
    const currentCowork = ApplicationsSelectors.currentCowork(
      state.applications
    );
    const branches = ApplicationsSelectors.allBranches(state.applications);
    const currentUserEmail = AuthSelectors.currentUserEmail(state.auth);
    const branchForCurrentUser = ApplicationsSelectors.branchForCurrentUserInCowork(
      currentUserEmail!,
      currentCowork
    );

    if (branches.length <= 1) {
      return false;
    } else if (branchForCurrentUser) {
      return false;
    } else {
      return true;
    }
  };

  public static allBranches = (state: IApplicationsState): ICoworkBranch[] =>
    state.branches || [];

  // tslint:disable-next-line:member-ordering
  public static currentUserBranchInCurrentCowork = createSelector(
    (state: IState) => AuthSelectors.currentUserEmail(state.auth),
    (state: IState) => ApplicationsSelectors.currentCowork(state.applications),
    ApplicationsSelectors.branchForCurrentUserInCowork
  );
}



// WEBPACK FOOTER //
// ./src/state/modules/applications/ApplicationsSelectors.ts